import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CURATION_SOURCE_OPTIONS,
  countActiveCurationFilters,
  DEFAULT_CURATION_FILTERS,
  type CurationFiltersState,
  type CurationVisibilityFilter,
} from "@/components/pages/guesteria-partners/utils/guesteriaCurationFilters";
import { GuesteriaServicesFilterButton } from "@/components/pages/guesteria-partners/components/GuesteriaServicesFilterButton";
import type { PartnerHotelCurationItemSource } from "@/types/partner-destination";

const VISIBILITY_OPTIONS: CurationVisibilityFilter[] = [
  "all",
  "visible",
  "hidden",
];

function parsePrice(value: string): number | null {
  if (value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export type GuesteriaCurationFiltersPopoverProps = {
  applied: CurationFiltersState;
  onApply: (next: CurationFiltersState) => void;
};

export function GuesteriaCurationFiltersPopover({
  applied,
  onApply,
}: GuesteriaCurationFiltersPopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<CurationFiltersState>(applied);
  const [minText, setMinText] = React.useState("");
  const [maxText, setMaxText] = React.useState("");
  const appliedCount = countActiveCurationFilters(applied);

  const toggleSource = (source: PartnerHotelCurationItemSource) => {
    setDraft((prev) => ({
      ...prev,
      sources: prev.sources.includes(source)
        ? prev.sources.filter((s) => s !== source)
        : [...prev.sources, source],
    }));
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDraft(applied);
          setMinText(applied.minPrice?.toString() ?? "");
          setMaxText(applied.maxPrice?.toString() ?? "");
        }
      }}
    >
      <PopoverTrigger asChild>
        <GuesteriaServicesFilterButton>
          {appliedCount > 0
            ? t("partners.curation.filters.titleWithCount", {
                count: appliedCount,
              })
            : t("partners.curation.filters.title")}
        </GuesteriaServicesFilterButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex w-[320px] flex-col gap-4 rounded-2xl border border-[#E5E5E5] bg-white p-4"
      >
        <h4 className="text-base font-semibold text-[#171717]">
          {t("partners.curation.filters.title")}
        </h4>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-[#171717]">
            {t("partners.curation.filters.visibility")}
          </span>
          {VISIBILITY_OPTIONS.map((option) => (
            <label
              key={option}
              className="flex cursor-pointer items-center gap-2 text-sm text-[#171717]"
            >
              <input
                type="radio"
                name="curation-visibility"
                checked={draft.visibility === option}
                onChange={() =>
                  setDraft((prev) => ({ ...prev, visibility: option }))
                }
              />
              {t(`partners.curation.filters.visibilityOptions.${option}`)}
            </label>
          ))}
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-[#171717]">
            {t("partners.curation.filters.source")}
          </span>
          {CURATION_SOURCE_OPTIONS.map((source) => (
            <label
              key={source}
              className="flex cursor-pointer items-center gap-2 text-sm text-[#171717]"
            >
              <Checkbox
                checked={draft.sources.includes(source)}
                onCheckedChange={() => toggleSource(source)}
              />
              {t(`partners.curation.filters.sourceOptions.${source}`)}
            </label>
          ))}
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-[#171717]">
            {t("partners.curation.filters.price")}
          </span>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              inputMode="decimal"
              placeholder={t("partners.curation.filters.priceMin")}
              aria-label={t("partners.curation.filters.priceMin")}
              value={minText}
              onChange={(e) => setMinText(e.target.value)}
            />
            <Input
              type="number"
              min={0}
              inputMode="decimal"
              placeholder={t("partners.curation.filters.priceMax")}
              aria-label={t("partners.curation.filters.priceMax")}
              value={maxText}
              onChange={(e) => setMaxText(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => {
              setDraft(DEFAULT_CURATION_FILTERS);
              setMinText("");
              setMaxText("");
              onApply(DEFAULT_CURATION_FILTERS);
            }}
          >
            {t("partners.curation.filters.reset")}
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={() => {
              let minPrice = parsePrice(minText);
              let maxPrice = parsePrice(maxText);
              if (
                minPrice !== null &&
                maxPrice !== null &&
                minPrice > maxPrice
              ) {
                [minPrice, maxPrice] = [maxPrice, minPrice];
              }
              onApply({ ...draft, minPrice, maxPrice });
              setOpen(false);
            }}
          >
            {t("partners.curation.filters.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
